import { DOCUMENT, ViewportScroller } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Inject, OnInit, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Store } from '@ngrx/store';
import { uniq as _uniq } from 'lodash-es';
import { zip as observableZip } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { isNullOrWhitespace, isNumeric } from 'helpers';

import { GetOrganizationDetailsGQL } from 'generated/graphql';

import { SET_ORG_NAME } from './_state/org-context/org-context-actions';
import { NavigationService } from 'core/navigation.service';
import { StyleLoaderService } from './style-loader.service';
import { environment } from 'environments/environment';
import { AppStateStorage } from 'core/state/app-state-storage';
import { StoreNames } from 'core/state/app-state.service';
import { SocialNetworksService } from './social/get-social-networks.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
  dataLoaded = false;
  minScrollStyle: any;
  currentHeight = 0;

  /**
   *
   */
  constructor(
    private _router: Router,
    private _viewportScroller: ViewportScroller,
    private getOrganizationDetails: GetOrganizationDetailsGQL,
    private _activatedRoute: ActivatedRoute,
    private _swUpdate: SwUpdate,
    private _cd: ChangeDetectorRef,
    private _titleService: Title,
    private _store: Store,
    private _navigationService: NavigationService,
    private _styleLoader: StyleLoaderService,
    private _renderer: Renderer2,
    private _socialNetworkService: SocialNetworksService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this._navigationService.startSaveHistory();
  }

  ngOnInit(): void {

    this._setTitle();
    this._setBodyClass();
    this._fixScroller();
    this._setOrganizationName();

    if (this._swUpdate.isEnabled) {
      const updatesAvailable = this._swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        map(evt => ({
          type: 'UPDATE_AVAILABLE',
          current: evt.currentVersion,
          available: evt.latestVersion,
        })));

      updatesAvailable.subscribe(() => {
        if (confirm('New version of Action Builder is available. Load New Version?')) {
          window.location.reload();
        }
      });
    }

    this.preloadData();
  }

  private preloadData() {
    this._socialNetworkService.getSocialNetworks().subscribe({
      next: () => {
        this.dataLoaded = true;
        this._cd.markForCheck();
      },
      error: () => {
        this.dataLoaded = true;
        this._cd.markForCheck();
      }
    });
  }

  /**
   * _setBodyClass
   *
   * Adds the appropriate className(s) to the body element
   * depending on the current url (for page-specific targeting).
   * So a url like this: actionbuilder.org/entities/list
   * would result in the body element having the following classNames:
   *  page--entities
   *  page--entities-list
   */
  private _setBodyClass() {
    // quick reference for renderer
    const r = this._renderer;
    // quick reference for body element
    const b = this.document.body;

    // is user opting for skinny anv? if so, apply appropriate classname to body element
    const appStorage = new AppStateStorage();
    if(appStorage.read(StoreNames.UseSkinnyNav) === 'true') {
      r.addClass(b, 'use-skinny-nav');
    }

    this._router.events.subscribe(value => {
      if(value instanceof NavigationEnd) {
        // get current url
        const url = window.location.pathname;
        // convert current url to array
        const urlParts = url.substr(1).split('/');
        // get current page className
        const currClass = b.getAttribute('data-page-class') || '';
        // convert current page className string to array
        const currClassArray = currClass.split(' ');
        // create className suffix variable
        let suffix = '';
        // create currClassData variable
        const currClassData: any[] = [];
        // remove classNames that were added on previous page load
        currClassArray.forEach(function(val) {
          if(val) {
            r.removeClass(b, val);
          }
        });
        // add new classNames
        urlParts.forEach(function(val) {
          // skip numeric url parts (eg. the '1000' in '/entity/view/1000/profile')
          if(!isNumeric(val)) {
            suffix = (suffix.length > 0) ? suffix + '-' + val : val;
            r.addClass(b, 'page--' + suffix);
            currClassData.push('page--' + suffix);
          }
        });
        // storing new classNames (for easy removal later)
        b.setAttribute('data-page-class', currClassData.join(' '));
      }
    });
  }

  private _setOrganizationName() {
    this.getOrganizationDetails.fetch()
      .subscribe(({ data }) => {
        if (data) {
          this._store.dispatch(
            SET_ORG_NAME({ payload: data.getOrganization })
          );
          this._cd.markForCheck();
        }
      });
  }

  private _setTitle() {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this._activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => observableZip(
          ...route.pathFromRoot.map(rd => rd.data),
          (...args) => args),
        )
      )
      .subscribe((data: any[]) => {
        let title = '';
        const titleTree = data
          .map((routeData) => routeData.title)
          .filter(t => !isNullOrWhitespace(t));

        _uniq(titleTree).forEach((t) => {
          title = `${title} ${t}`;
        });

        if (title === '') {
          title = 'Action Builder';
        }
        this._titleService.setTitle(`${title} - Action Builder`);
        this._cd.markForCheck();
      });
  }

  /**
   * Set the min height of page to be
   * able to handle the scroll restore.
   * This is important to do because
   * Angular might try to scroll before the data is loaded.
   */
  private _fixScroller() {
    this._router.events.pipe(
      filter(event => event instanceof Scroll)
    ).subscribe((event: Scroll) => {
      if (event.position == null) {
        this.minScrollStyle = null;
        return;
      }

      this.minScrollStyle = { 'min-height': event.position[1] + 100 + 'px' };
      this._cd.markForCheck();
      setTimeout(() => this._viewportScroller.scrollToPosition(event.position));
    });
  }
}
